import { Component } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { LEAD_BASED_PAINT } from '@shared/utils/app-static-data';
import { PublicComplaintSubmission, PublicComplaintSubmissionReceipt } from 'app/models/complaints/public-complaint-submission';
import { PublicComplaintService } from 'app/services/complaints/public-complaint.service';
import { SharedService } from 'app/services/core/shared.service';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-complaint-submission-dialog',
  templateUrl: './complaint-submission-dialog.component.html',
  styleUrls: ['./complaint-submission-dialog.component.scss']
})
export class ComplaintSubmissionDialogComponent {
  private readonly zipPattern = /^\d{5}(-\d{4})?$/;
  private readonly phonePattern = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})(\s*(x|ext)\s*\d+)?$/i;

  public readonly uiData = LEAD_BASED_PAINT;
  public readonly complaintForm = new UntypedFormGroup({
    submitterFirstName: new UntypedFormControl(''),
    submitterLastName: new UntypedFormControl(''),
    submitterPhone: new UntypedFormControl('', [Validators.pattern(this.phonePattern)]),
    submitterEmail: new UntypedFormControl('', [Validators.email]),
    submitterAddressStreet: new UntypedFormControl(''),
    submitterAddressCity: new UntypedFormControl(''),
    submitterAddressState: new UntypedFormControl('OR'),
    submitterAddressZip: new UntypedFormControl('', [Validators.pattern(this.zipPattern)]),
    locationStreet: new UntypedFormControl('', [Validators.required]),
    locationCity: new UntypedFormControl('', [Validators.required]),
    locationState: new UntypedFormControl('OR', [Validators.required]),
    locationZip: new UntypedFormControl('', [Validators.pattern(this.zipPattern)]),
    locationCounty: new UntypedFormControl(''),
    peopleInvolved: new UntypedFormControl(''),
    narrative: new UntypedFormControl('', [Validators.required]),
  });

  public submitting: boolean = false;
  public submitted: boolean = false;
  public receipt: PublicComplaintSubmissionReceipt | null = null;

  constructor(
    public sharedService: SharedService,
    private readonly dialogRef: MatDialogRef<ComplaintSubmissionDialogComponent>,
    private readonly complaintService: PublicComplaintService,
    private readonly toastr: ToastrService,
  ) {}

  get showLocationCounty(): boolean {
    return this.complaintForm.get('locationState')?.value === 'OR';
  }

  submit(): void {
    if (this.submitting) {
      return;
    }

    if (this.complaintForm.invalid) {
      this.complaintForm.markAllAsTouched();
      this.toastr.warning(
        'Please complete all required fields and correct any invalid entries before submitting.',
        'Complaint Not Submitted',
      );
      return;
    }

    this.submitting = true;
    this.complaintService.submitComplaint(this.complaintForm.getRawValue() as PublicComplaintSubmission)
      .pipe(finalize(() => {
        this.submitting = false;
      }))
      .subscribe({
        next: (receipt) => {
          if (!receipt || !receipt.id) {
            this.toastr.error('Unable to confirm the complaint submission. Please try again.', 'Complaint Submission Failed');
            return;
          }

          this.receipt = receipt;
          this.submitted = true;
          this.complaintForm.disable({ emitEvent: false });
          this.toastr.success('Your complaint was submitted successfully.', 'Complaint Submitted');
        },
        error: (error) => {
          this.toastr.error(this.getSubmitErrorMessage(error), 'Complaint Submission Failed');
        },
      });
  }

  cancel(): void {
    if (!this.submitting) {
      this.dialogRef.close();
    }
  }

  closeAfterSuccess(): void {
    this.dialogRef.close(this.receipt);
  }

  onLocationStateChanged(): void {
    if (!this.showLocationCounty) {
      this.complaintForm.get('locationCounty')?.setValue('');
    }
  }

  private getSubmitErrorMessage(error: any): string {
    if (typeof error?.error === 'string' && error.error.trim()) {
      return error.error;
    }

    if (typeof error?.error?.msg === 'string' && error.error.msg.trim()) {
      return error.error.msg;
    }

    if (typeof error?.error?.message === 'string' && error.error.message.trim()) {
      return error.error.message;
    }

    if (typeof error?.message === 'string' && error.message.trim()) {
      return error.message;
    }

    return 'Unable to submit the complaint right now. Please try again.';
  }
}